import { GiftingCard } from '@components/product/GiftingBlock'
import cn from 'clsx'
import { FC } from 'react'
import { connectHits, Pagination } from 'react-instantsearch-dom'

const GRID_LAYOUT_4 = 'GRID_LAYOUT_4'
const GRID_LAYOUT_1 = 'GRID_LAYOUT_1'

// Inject a gifting promo tile at the 12th and 28th positions (0-based indexes).
// The trailing products taken by the tiles are dropped (e.g. 36 -> 34 products).
// Desktop renders both tiles (36 total); mobile only renders the first — the
// second is desktop-only — so mobile shows 35 total.
const GIFTING_POSITIONS = [11, 28]

type GridCell =
  | { type: 'gift'; key: string }
  | { type: 'gift-desktop'; key: string }
  | { type: 'product'; key: string; hit: any }

interface props {
  hits: any[]
  HitComponent: any
  hasMore: boolean
  refineNext: any
  totalProducts?: any
  gridSelectionDesktop?: any
  gridSelectionMobile?: any
  pathname?: any
  page: number
}

const AlgoliaCustomHits: FC<props> = ({
  hits,
  HitComponent,
  totalProducts,
  gridSelectionDesktop,
  gridSelectionMobile,
  pathname,
  page,
}) => {
  const gridDesktop =
    gridSelectionDesktop === GRID_LAYOUT_4 ? `lg:grid-cols-4` : `lg:grid-cols-6`
  const gridMobile =
    gridSelectionMobile === GRID_LAYOUT_1 ? `grid-cols-1` : `grid-cols-2`
  // The 6-column grid renders a condensed gifting tile (no feature icons).
  const isSixColDesktop = gridSelectionDesktop !== GRID_LAYOUT_4
  // In the single-column mobile layout the tile is already full width, so it
  // must not span 2 columns (that would create a phantom second column).
  const isOneColMobile = gridSelectionMobile === GRID_LAYOUT_1

  let filteredHits
  if (pathname && pathname.includes('collections')) {
    //hides sold out products from collection pages
    filteredHits = hits.filter((hit: any) => hit?.variants_inventory_count > 0)
  } else {
    //only displays a sold out product if it's the closest search match
    filteredHits = hits.filter((hit: any, index) => {
      return hit?.variants_inventory_count || index === 0
    })
  }
  // const modifier = page - 1;
  // let bottomRange = page == 1 ? 1 : (page - 1 * 36) + 1;
  // let topRange = page == 1 ? hits.length : bottomRange + hits.length
  // const currentProductRange = `${bottomRange} - ${topRange}`

  // Place gifting tiles where products would otherwise sit. The first active
  // position shows on every viewport; any later position is desktop-only, so
  // mobile renders one fewer tile there. Only trailing products are dropped.
  const activeGiftPositions = GIFTING_POSITIONS.filter(
    (pos) => filteredHits.length > pos
  )
  const giftCount = activeGiftPositions.length
  // Drop the trailing products taken by the gift tiles (e.g. 36 -> 34).
  const products = filteredHits.slice(
    0,
    Math.max(filteredHits.length - giftCount, 0)
  )

  const gridCells: GridCell[] = []
  let productIndex = 0
  const totalTiles = products.length + giftCount
  for (let i = 0; i < totalTiles; i++) {
    const giftRank = activeGiftPositions.indexOf(i)
    if (giftRank === 0) {
      gridCells.push({ type: 'gift', key: `gift-${i}` })
    } else if (giftRank > 0) {
      // Desktop-only gift; hidden on mobile (mobile shows one fewer tile).
      gridCells.push({ type: 'gift-desktop', key: `gift-${i}` })
    } else {
      const hit = products[productIndex++]
      gridCells.push({ type: 'product', key: hit?.objectID ?? `p-${i}`, hit })
    }
  }

  return (
    <div>
      <div
        className={cn(
          `pb-11 pt-6 grid grid-flow-row-dense ${gridMobile} ${gridDesktop} gap-5 gap-y-8 lg:gap-8 lg:gap-y-10`
        )}
      >
        {gridCells.map((cell) => {
          if (cell.type === 'gift' || cell.type === 'gift-desktop') {
            return (
              <div
                key={cell.key}
                className={cn({
                  'col-span-2 lg:col-span-1':
                    cell.type === 'gift' && !isOneColMobile,
                  'hidden lg:block': cell.type === 'gift-desktop',
                })}
              >
                <GiftingCard sixColumnDesktop={isSixColDesktop} />
              </div>
            )
          }
          return (
            <div key={cell.key}>
              <HitComponent
                hit={cell.hit}
                isSoldOut={cell.hit?.variants_inventory_count === 0}
              />
            </div>
          )
        })}
      </div>
      <div className="overflow-x-auto pb-8 pt-5 lg:pt-10 lg:pb-10 body-2 text-text-subdued">
        <div className="flex justify-center">
          {
            // https://www.algolia.com/doc/deprecated/instantsearch/react/v6/api-reference/pagination/
          }
          <Pagination
            translations={{
              previous: 'Prev',
              next: 'Next',
              first: 'First',
              last: 'Last',
            }}
          />
          <style jsx global>
            {`
              .ais-Pagination-list {
                display: flex;
              }
              .ais-Pagination-link {
                padding: 10px 15px;
                border: solid 1px #f5f5f5;
              }
              .ais-Pagination-link--selected {
                background-color: #f5f5f5;
                font-weight: 600;
              }
              .ais-Pagination {
                flex-shrink: 0;
              }
            `}
          </style>
        </div>
      </div>
      {/*<div className="lg:pb-20">*/}
      {/*  <div className="body-2 text-text-subdued flex justify-center">*/}
      {/*    Showing <span className="px-1">{currentProductRange}</span> of{' '}*/}
      {/*    <span className="pl-1">{totalProducts}</span>*/}
      {/*  </div>*/}
      {/*  {filteredHits.length > 0 && <StatsWithProgressBar hits={filteredHits} />}*/}
      {/*</div>*/}
    </div>
  )
}

export default connectHits(AlgoliaCustomHits)
